import { TimePickerValue } from '@app/shared/types/models/shared/time-picker-value';
import { parse, toSeconds } from 'iso8601-duration';
import moment from 'moment';
import { TimeUnits } from '../types/enums/time-units.enum';
import { TimeZone } from '../types/enums/time-zone';

export const IANA_TIME_ZONES: Record<TimeZone, string> = {
  Oslo: 'Europe/Oslo',
  Utc: 'UTC',
  Warsaw: 'Europe/Warsaw',
  Dublin: 'Europe/Dublin'
};

/**
 * Parses an ISO 8601 duration string (e.g. `PT1H30M`) and returns its length in seconds.
 */
export function convertTimeDurationToSeconds(duration: string): number {
  return toSeconds(parse(duration));
}

/**
 * Builds an ISO 8601 duration from the elapsed time between two clock values (same day).
 *
 * @param fromTime - Start time (hours and minutes).
 * @param toTime - End time (hours and minutes).
 * @returns ISO duration string, e.g. `PT1H30M` when from 08:00 to 09:30.
 */
export function convertTimePickerRangeToIsoTime(fromTime: TimePickerValue, toTime: TimePickerValue): string {
  return moment
    .duration(
      moment({ hours: toTime.hours, minutes: toTime.minutes }).diff(
        moment({ hours: fromTime.hours, minutes: fromTime.minutes })
      )
    )
    .toISOString();
}

/**
 * Converts a fixed hours-and-minutes value into an ISO 8601 duration string.
 */
export function convertHourToIsoTime(hour: number, minutes: number): string {
  return moment.duration({ hour: hour, minutes: minutes }).toISOString();
}

/**
 * Builds an ISO 8601 duration from a user-entered amount and unit.
 *
 * @param amount - Duration value; `null`, zero, or negative yields `undefined`.
 * @param units - {@link TimeUnits.MINUTES}, {@link TimeUnits.HOURS}, or {@link TimeUnits.MAN_DAYS} (8 h per day).
 * @returns ISO duration string, or `undefined` when the amount is not positive.
 */
export function amountAndUnitToDuration(amount: number | null, units: TimeUnits): string | undefined {
  if (amount === null || amount <= 0) {
    return undefined;
  }

  switch (units) {
    case TimeUnits.MINUTES:
      return moment.duration({ minutes: amount }).toISOString();
    case TimeUnits.MAN_DAYS:
      return moment.duration({ hours: amount * 8 }).toISOString();
    case TimeUnits.HOURS:
    default:
      return moment.duration({ hours: amount }).toISOString();
  }
}
/**
 * Formats a duration in seconds as a compact human-readable string (`Xh Ymin`).
 *
 * @param seconds - Total seconds; `undefined`, `0`, or falsy values yield `0h 0min`.
 * @returns e.g. `1h 30min`, `2h`, or `45min` (minutes omitted when hours are shown and minutes are 0).
 */
export function formatTime(seconds: number | undefined): string {
  if (!seconds) return '0h 0min';

  const totalMinutes = Math.floor(seconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  let result = '';
  if (hours > 0) {
    result += `${hours}h `;
  }
  if (minutes > 0 || hours === 0) {
    result += `${minutes}min`;
  }

  return result.trim();
}

/**
 * Converts an ISO 8601 duration string to decimal hours for display or aggregation.
 *
 * @param duration - ISO duration (e.g. `PT2H`, `PT30M`); empty or invalid input returns `0`.
 * @returns Hours as a number (e.g. `1.5` for `PT1H30M`); zero-duration strings return `0`.
 */
export function durationToHours(duration?: string): number {
  return duration ? moment.duration(duration).asHours() : 0;
}

/**
 * Formats the date part of an instant as observed in a reporting time zone, e.g. `27 Aug 2026`.
 *
 * @param value - Instant to format; `undefined` yields an empty string.
 * @param timeZone - Reporting time zone; `undefined` falls back to the browser time zone.
 */
export function formatDateInTimeZone(value: Date | undefined, timeZone: TimeZone | undefined): string {
  if (!value) {
    return '';
  }

  return new Intl.DateTimeFormat(navigator.language, {
    dateStyle: 'medium',
    timeZone: timeZone ? IANA_TIME_ZONES[timeZone] : undefined
  }).format(value);
}

/**
 * Formats the clock time of an instant as observed in a reporting time zone, e.g. `12:38`.
 *
 * @param value - Instant to format; `undefined` yields an empty string.
 * @param timeZone - Reporting time zone; `undefined` falls back to the browser time zone.
 */
export function formatTimeInTimeZone(value: Date | undefined, timeZone: TimeZone | undefined): string {
  if (!value) {
    return '';
  }

  return new Intl.DateTimeFormat(navigator.language, {
    timeStyle: 'short',
    timeZone: timeZone ? IANA_TIME_ZONES[timeZone] : undefined
  }).format(value);
}

/**
 * Formats the date and clock time of an instant as observed in a reporting time zone.
 *
 * @param value - Instant to format; `undefined` yields an empty string.
 * @param timeZone - Reporting time zone; `undefined` falls back to the browser time zone.
 */
export function formatDateTimeInTimeZone(value: Date | undefined, timeZone: TimeZone | undefined): string {
  const date = formatDateInTimeZone(value, timeZone);
  const time = formatTimeInTimeZone(value, timeZone);

  if (!date) {
    return '';
  }

  return time ? `${date} ${time}` : date;
}
